import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { getAal } from './useMfa';
import { isSchichtleitung, type StaffRole } from '@sizilianer/core/features/auth/roles';
import { toSupabaseError } from '@/utils/supabaseError';

/**
 * A-002: Auth-Vorab-Checks gegen nie-auflösende Promises absichern.
 * Bei Timeout wird rejected → die bestehenden Fail-open-`catch`-Zweige
 * setzen den State auf 'ok' (statt unbegrenzt im Spinner zu hängen).
 */
function withTimeout<T>(promise: PromiseLike<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    Promise.resolve(promise),
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms),
    ),
  ]);
}

/** 'chatbot' (0067) = externer Bot-Betreiber — steht AUSSERHALB der
 *  Rang-Hierarchie; sieht nur /admin/chatbot (RequireAuth allowChatbot).
 *  0.12: Typ lebt in core (adminNav braucht ihn dort), hier re-exportiert. */
export type { StaffRole } from '@sizilianer/core/features/auth/roles';

export type StaffProfile = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  role: StaffRole;
  active: boolean;
  is_service_admin: boolean;
  login_kind: 'email' | 'pin';
  /** true = Initialpasswort, Wechsel bei Erstanmeldung erzwungen (0055). */
  must_change_password: boolean;
};

export type MfaState = 'checking' | 'challenge_required' | 'ok';
export type OnboardingState = 'checking' | 'redirect_required' | 'ok';
/**
 * Rein aus dem Profil abgeleitet (kein eigener Async-Check nötig):
 * change_required nur bei login_kind='email' — PIN-Kellner haben kein Passwort.
 */
export type PasswordState = 'change_required' | 'ok';

export type AuthContextValue = {
  // Session-State
  session: Session | null;
  user: User | null;
  profile: StaffProfile | null;
  role: StaffRole | null;
  loading: boolean;
  /**
   * true, sobald das Profil zum AKTUELLEN user geladen wurde (auch wenn es
   * null blieb). Beim frischen Login setzt onAuthStateChange den user synchron,
   * lädt das Profil aber deferred — in diesem Fenster ist profileReady false
   * und RequireAuth zeigt den Spinner statt fälschlich „kein Profil" zu werfen.
   */
  profileReady: boolean;
  /** true = MFA-/Onboarding-Check gehören zum AKTUELLEN user (C4.4). */
  mfaReady: boolean;
  onboardingReady: boolean;

  // Derived
  isAuthenticated: boolean;
  isAdmin: boolean;
  isManagerOrAdmin: boolean;
  isServiceAdmin: boolean;

  // MFA-State (einmal initial geprüft, dann stabil)
  mfaState: MfaState;

  // Onboarding-State (einmal initial geprüft, dann stabil)
  onboardingState: OnboardingState;

  // Passwort-Zwang (rein aus dem Profil abgeleitet)
  passwordState: PasswordState;

  // Actions
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;

  /** Re-trigger Onboarding-Check (z.B. nach Onboarding-Wizard fertig). */
  refreshOnboardingState: () => Promise<void>;
  /** Re-trigger MFA-Check (z.B. nach 2FA-Challenge). */
  refreshMfaState: () => Promise<void>;
  /** Profil neu laden (z.B. nach erzwungenem Passwortwechsel). */
  refreshProfile: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

// Ausstiegsluke — die BEGRÜNDUNG war falsch: `onboarding_completed_at` (0020)
// steht längst in den generierten Typen. Die Luke bleibt vorerst; sie gehört
// zum eigenen Roadmap-Punkt „packages/core typisieren", nicht hierher.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb = supabase as any;

type AuthProviderProps = {
  children: ReactNode;
};

/**
 * AuthProvider — zentraler Auth-State für die ganze App.
 *
 * Behebt das "Lade-Loop"-Problem (Item 1 aus Bug-Sprint):
 *
 * Vorher: useAuth() war ein lokaler Hook in JEDEM Component-Mount → bei jedem
 * Nav-Wechsel (lazy + Suspense + RequireAuth) wurde getSession() + loadProfile()
 * + getAal() + onboarding-Query erneut ausgeführt. RequireAuth zeigte den Spinner
 * während die 3 Async-Pipelines liefen.
 *
 * Jetzt: AuthProvider macht alle 3 Pipelines EINMAL bei App-Mount + bleibt im
 * Context. RequireAuth und alle anderen useAuth()-Consumer lesen nur Snapshot —
 * keine neuen DB-Calls bei Re-Mount.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<StaffProfile | null>(null);
  // Invariante: die User-ID, zu der `profile` gehört (null = kein Load erfolgt).
  // Wird an ALLEN setProfile-Stellen mitgeführt; profileReady leitet sich daraus
  // ab, statt ein eigener Zustandsautomat zu sein (kein „vergessener" Übergang
  // bei StrictMode/HMR, kein Flackern bei TOKEN_REFRESHED — gleiche ID ⇒ ready).
  const [profileUserId, setProfileUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [mfaState, setMfaState] = useState<MfaState>('checking');
  const [onboardingState, setOnboardingState] = useState<OnboardingState>('checking');
  // C4.4: Für welchen User wurde der jeweilige Check zuletzt abgeschlossen?
  // Abgeleitetes Ready statt Zustandsautomat (gleiche Idee wie profileReady) —
  // ohne das hielten mfaState/onboardingState nach dem Login kurz das stale
  // 'ok' des anonymen Zustands → Dashboard-Blitz vor dem 2FA-/Onboarding-Redirect.
  const [mfaForUserId, setMfaForUserId] = useState<string | null>(null);
  const [onboardingForUserId, setOnboardingForUserId] = useState<string | null>(null);

  const loadProfile = useCallback(
    async (userId: string): Promise<StaffProfile | null> => {
      // login_kind (0044) / must_change_password (0055) noch nicht in
      // Database-Types → sb (any).
      const { data, error } = await sb
        .from('staff_profiles')
        .select('id, first_name, last_name, role, active, is_service_admin, login_kind, must_change_password')
        .eq('id', userId)
        .maybeSingle();
      if (error) {
        console.error('[AuthProvider] loadProfile error', error);
        return null;
      }
      // Alt-Zeilen: login_kind fehlt → 'email'; must_change_password fehlt → false
      if (data && !data.login_kind) data.login_kind = 'email';
      if (data && typeof data.must_change_password !== 'boolean') data.must_change_password = false;
      return data as StaffProfile | null;
    },
    [],
  );

  // --- Session + Profile bei Mount (einmalig) + onAuthStateChange Subscription ---
  useEffect(() => {
    let mounted = true;

    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      const sess = data.session;
      if (sess?.user) {
        const p = await loadProfile(sess.user.id);
        if (!mounted) return;
        setSession(sess);
        setUser(sess.user);
        setProfile(p);
        setProfileUserId(sess.user.id);
      } else {
        setSession(null);
        setUser(null);
        setProfile(null);
        setProfileUserId(null);
      }
      setLoading(false);
    })();

    const { data: subscription } = supabase.auth.onAuthStateChange(
      // WICHTIG: KEINE await-Supabase-Aufrufe direkt im Callback. Supabase hält
      // während des Callbacks den Auth-Lock (navigator.locks); ein hier
      // ausgeführter Supabase-Call (loadProfile) braucht den Lock erneut →
      // re-entranter Deadlock, der Lock wird nie freigegeben und das Backoffice
      // hängt dauerhaft im RequireAuth-Spinner. Daher: Session/User synchron
      // setzen, Profil-Load via setTimeout(…,0) aus dem Lock-Scope herausschieben.
      (_event, sess) => {
        if (!mounted) return;
        if (sess?.user) {
          const u = sess.user;
          setSession(sess);
          setUser(u);
          setTimeout(() => {
            void (async () => {
              const p = await loadProfile(u.id);
              if (!mounted) return;
              setProfile(p);
              setProfileUserId(u.id);
              setLoading(false);
            })();
          }, 0);
        } else {
          setSession(null);
          setUser(null);
          setProfile(null);
          // Pflicht: sonst wäre beim Re-Login DESSELBEN Users profileReady
          // sofort true bei profile=null → der Fehl-Toast käme zurück.
          setProfileUserId(null);
          // bei logout: MFA + Onboarding zurücksetzen
          setMfaState('ok');
          setOnboardingState('ok');
          setLoading(false);
        }
      },
    );

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, [loadProfile]);

  // --- MFA-Check (einmal pro Login) ---
  const refreshMfaState = useCallback(async () => {
    if (!user) {
      setMfaState('ok');
      return;
    }
    try {
      const { currentLevel, nextLevel } = await withTimeout(getAal(), 6000, 'getAal');
      if (nextLevel === 'aal2' && currentLevel === 'aal1') {
        setMfaState('challenge_required');
      } else {
        setMfaState('ok');
      }
    } catch (err) {
      console.error('[AuthProvider] getAal failed — fail-open to ok', err);
      setMfaState('ok');
    }
  }, [user]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setMfaState('ok');
      setMfaForUserId(null);
      return;
    }
    const uid = user.id;
    void refreshMfaState().then(() => setMfaForUserId(uid));
  }, [user, loading, refreshMfaState]);

  // --- Onboarding-Check (einmal pro Login, nur für admin-Profile) ---
  const refreshOnboardingState = useCallback(async () => {
    if (!user || !profile) {
      setOnboardingState('ok');
      return;
    }
    // Service-Admin wird NIE auf Onboarding geleitet
    if (profile.is_service_admin) {
      setOnboardingState('ok');
      return;
    }
    // Nur admins werden auf Onboarding geleitet
    if (profile.role !== 'admin') {
      setOnboardingState('ok');
      return;
    }
    try {
      const { data, error } = await withTimeout<{
        data: { onboarding_completed_at: string | null } | null;
        error: { message: string } | null;
      }>(
        sb
          .from('restaurant_settings')
          .select('onboarding_completed_at')
          .eq('id', 1)
          .maybeSingle(),
        6000,
        'onboarding',
      );
      if (error) {
        console.error('[AuthProvider] onboarding query failed — fail-open', error);
        setOnboardingState('ok');
        return;
      }
      if (!data) {
        console.warn('[AuthProvider] restaurant_settings.id=1 missing — fail-open');
        setOnboardingState('ok');
        return;
      }
      if (data.onboarding_completed_at) {
        setOnboardingState('ok');
      } else {
        setOnboardingState('redirect_required');
      }
    } catch (err) {
      console.error('[AuthProvider] onboarding catch — fail-open', err);
      setOnboardingState('ok');
    }
  }, [user, profile]);

  useEffect(() => {
    if (loading) return;
    if (!user || !profile) {
      setOnboardingState('ok');
      setOnboardingForUserId(user?.id ?? null);
      return;
    }
    const uid = user.id;
    void refreshOnboardingState().then(() => setOnboardingForUserId(uid));
  }, [user, profile, loading, refreshOnboardingState]);

  // --- Action-Helper ---
  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw toSupabaseError(error);
    },
    [],
  );

  // Magic-Link-Login wurde bewusst ENTFERNT (Kundenentscheidung 2026-07-21):
  // Anmeldung nur noch per E-Mail+Passwort (Initialpasswort + Pflichtwechsel)
  // bzw. PIN. ⚠️ Der PIN-Login mintet seine Session serverseitig weiterhin über
  // generateLink — Magic-Links dürfen deshalb NIE auf Supabase-Projektebene
  // deaktiviert werden, nur dieser UI-Weg ist weg.

  /**
   * Abmelden — DIESES Gerät, nicht das Konto.
   *
   * ⚠️ `scope: 'local'` ist hier der Punkt, nicht ein Detail. auth-js hat
   * den Default `scope: 'global'` (`GoTrueClient.js:3398`, Fassung
   * 2.111.0) und widerruft damit ALLE Refresh-Tokens des Kontos — auf
   * jedem Gerät. Bis hierher stand hier ein `signOut()` ohne Argument.
   *
   * Zwei Aufrufer hängen daran, und bei beiden war das falsch:
   *  - der „Abmelden"-Knopf in `AdminLayout` — gemeint ist dieser
   *    Browser, nicht das Konto,
   *  - `AutoLockOverlay` nach 15 Minuten Untätigkeit. Das ist eine
   *    SPERRE. Wer den Laptop zuklappt, während dieselbe Kennung auf
   *    einem anderen Gerät im Dienst ist, hat dieses Gerät bisher
   *    mit hinausgeworfen.
   *
   * DER PASS macht es seit 2.4b richtig (`apps/pass/src/lib/signOut.ts`)
   * — das Web war die letzte Stelle mit dem Default. Wichtig wird es
   * mit dem Kontor (2.6): dort läuft dieses Backoffice IN der App und
   * unter DERSELBEN Sitzung; ein globales Abmelden risse dann die
   * Servicefläche am Tresen mit.
   *
   * Ein „auf allen Geräten abmelden" gibt es bewusst nicht — es wäre
   * `{ scope: 'global' }` und braucht eine eigene, benannte Fläche,
   * keinen stillen Default.
   */
  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) throw toSupabaseError(error);
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const siteUrl =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'https://die-sizilianer.de';
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${siteUrl}/admin/reset-password`,
    });
    if (error) throw toSupabaseError(error);
  }, []);

  // Profil neu laden — z.B. nachdem der Pflicht-Passwortwechsel das Flag
  // serverseitig zurückgesetzt hat, damit der Guard sofort freigibt.
  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const p = await loadProfile(user.id);
    setProfile(p);
  }, [user, loadProfile]);

  const role = profile?.role ?? null;
  // Abgeleitet, kein eigener Zustand: ready, sobald das Profil zum aktuellen
  // user geladen wurde (auch wenn es null blieb — dann ist das die Wahrheit).
  const profileReady = !user || profileUserId === user.id;
  const mfaReady = !user || mfaForUserId === user.id;
  const onboardingReady = !user || onboardingForUserId === user.id;
  const isAuthenticated = !!user && !!profile?.active;
  const isAdmin = role === 'admin';
  // Dieselbe Regel wie im PASS: seit 8b106c8 liegt sie als
  // isSchichtleitung() in core, fail-closed für null/undefined, mit Test
  // und gefahrener Negativ-Probe. Der Ausdruck hier war damit die vierte
  // handgeschriebene Kopie — inhaltlich zeichengleich, aber ohne Wächter.
  const isManagerOrAdmin = isSchichtleitung(role);
  const isServiceAdmin = profile?.is_service_admin === true;
  // Rein abgeleitet, kein Async: PIN-Nutzer sind ausgenommen (kein Passwort).
  const passwordState: PasswordState =
    profile && profile.must_change_password && profile.login_kind === 'email'
      ? 'change_required'
      : 'ok';

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      profile,
      role,
      loading,
      profileReady,
      mfaReady,
      onboardingReady,
      isAuthenticated,
      isAdmin,
      isManagerOrAdmin,
      isServiceAdmin,
      mfaState,
      onboardingState,
      passwordState,
      signInWithPassword,
      signOut,
      resetPassword,
      refreshOnboardingState,
      refreshMfaState,
      refreshProfile,
    }),
    [
      session,
      user,
      profile,
      role,
      loading,
      profileReady,
      mfaReady,
      onboardingReady,
      isAuthenticated,
      isAdmin,
      isManagerOrAdmin,
      isServiceAdmin,
      mfaState,
      onboardingState,
      passwordState,
      signInWithPassword,
      signOut,
      resetPassword,
      refreshOnboardingState,
      refreshMfaState,
      refreshProfile,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
